/**
 * What counts as a Chair, and what counts as a free one.
 *
 * A Chair's `status` has been written three different ways over the life of
 * this code — `'empty'` by the Gala table builders, `'vacant'` by the floor-map
 * ones, and not at all by Designs saved before `status` was serialised — while
 * every reader matched the single literal `'empty'`. A Table whose Chairs came
 * from any other writer then looked full even though nobody was sitting in it.
 *
 * So occupancy is read the other way round: a Chair is taken only when it says
 * so (`status === 'occupied'`, or it carries `attendeeData`). Everything else is
 * free. Adding another writer can no longer make a Table unassignable.
 */

/**
 * A Chair as this file needs to see it. Structural on purpose: importing Fabric
 * here would pull its native canvas binding into every test that touches
 * seating, and nothing below needs more than these props.
 */
export interface ChairLike {
  type?: string;
  isChair?: boolean;
  status?: string;
  attendeeData?: unknown;
}

/** A Table as this file needs to see it. */
export interface TableLike {
  getObjects?: () => ChairLike[];
}

export const isChairObject = (obj: ChairLike | undefined | null): boolean =>
  !!obj && obj.isChair === true;

/** Whether a Chair has an attendee drawn into it. */
export const isOccupiedChair = (obj: ChairLike | undefined | null): boolean => {
  if (!isChairObject(obj)) return false;
  return obj!.status === 'occupied' || !!obj!.attendeeData;
};

/** Whether a Chair is free, whatever the writer called its empty state. */
export const isEmptyChair = (obj: ChairLike | undefined | null): boolean =>
  isChairObject(obj) && !isOccupiedChair(obj);

/** Every Chair on a Table, occupied or not. */
export const chairsOf = (table: TableLike): ChairLike[] =>
  table?.getObjects?.().filter(isChairObject) ?? [];

/** The first free Chair on a Table, or `undefined` when it is full. */
export const findEmptyChair = (table: TableLike): ChairLike | undefined =>
  table?.getObjects?.().find(isEmptyChair);
